import { useState, useEffect, useRef } from 'react';
import { aquaApi } from './aquaApi';

const SETTING_KEY = 'marketPrices';

const DEFAULT_VARIETIES = [
  { variety: 'Tiger', grade: '21/30', price: '' },
  { variety: 'Vannamei', grade: '31/40', price: '' },
];

export default function MarketWidget({ theme }) {
  const [prices,   setPrices]   = useState(null);   // null = loading
  const [editing,  setEditing]  = useState(false);
  const [rows,     setRows]     = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const panelRef = useRef(null);
  const isDark = theme === 'dark';

  /* ── styles ─────────────────────────────────────────────── */
  const pill = {
    borderRadius: 20, fontSize: 12, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 13px', border: 'none',
  };
  const ghost = {
    ...pill,
    background: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.06)',
    color: isDark ? '#c8d8e8' : '#444',
    border: `1px solid ${isDark ? 'rgba(255,255,255,.15)' : 'rgba(0,0,0,.12)'}`,
  };

  /* ── load ────────────────────────────────────────────────── */
  useEffect(() => {
    aquaApi.getSettings()
      .then(s => {
        if (s?.[SETTING_KEY]) {
          try {
            const parsed = JSON.parse(s[SETTING_KEY]);
            setPrices(parsed.rows || []);
            setUpdatedAt(parsed.ts || null);
          } catch (_) { setPrices([]); }
        } else {
          setPrices([]);
        }
      })
      .catch(() => setPrices([]));
  }, []);

  /* close panel on outside click */
  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  /* ── open edit ───────────────────────────────────────────── */
  const openEdit = () => {
    setRows(prices && prices.length > 0
      ? prices.map(r => ({ ...r }))
      : DEFAULT_VARIETIES.map(r => ({ ...r })));
    setEditing(true);
  };

  /* ── row helpers ─────────────────────────────────────────── */
  const setRow = (i, field, val) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const addRow = () =>
    setRows(prev => [...prev, { variety: '', grade: '', price: '' }]);

  const removeRow = (i) =>
    setRows(prev => prev.filter((_, idx) => idx !== i));

  /* ── save ────────────────────────────────────────────────── */
  const handleSave = async () => {
    const valid = rows.filter(r => r.variety.trim() && r.price.trim());
    setSaving(true);
    try {
      const payload = JSON.stringify({ rows: valid, ts: Date.now() });
      await aquaApi.saveSetting({ key: SETTING_KEY, value: payload });
      setPrices(valid);
      setUpdatedAt(Date.now());
      setEditing(false);
    } catch (_) {}
    finally { setSaving(false); }
  };

  /* ── render ──────────────────────────────────────────────── */
  if (prices === null) return null;

  const hasPrices = prices.length > 0;
  const updatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>

      {/* ── Trigger pill ── */}
      {!editing && (
        hasPrices ? (
          <button onClick={openEdit}
            title={updatedLabel ? `Updated: ${updatedLabel}` : 'Market rates — click to edit'}
            style={{ ...ghost, gap: 10, padding: '5px 14px', flexWrap: 'nowrap', maxWidth: 340, overflow: 'hidden' }}>
            <span style={{ fontSize: 15 }}>📊</span>
            <div style={{ display: 'flex', gap: 10, overflow: 'hidden', flexWrap: 'nowrap' }}>
              {prices.map((r, i) => (
                <span key={i} style={{ whiteSpace: 'nowrap', fontWeight: 600,
                  color: isDark ? '#7dd3fc' : '#0369a1', fontSize: 12 }}>
                  {r.variety} <span style={{ color: isDark ? '#86efac' : '#15803d' }}>₹{r.price}</span>
                  {i < prices.length - 1 && <span style={{ color: isDark ? '#475569' : '#cbd5e1', marginLeft: 10 }}>·</span>}
                </span>
              ))}
            </div>
            <span style={{ fontSize: 11, opacity: 0.5 }}>✏️</span>
          </button>
        ) : (
          <button onClick={openEdit} style={{ ...ghost }}>
            📊 Set market prices
          </button>
        )
      )}

      {/* ── Edit panel ── */}
      {editing && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 999,
          background: isDark ? '#1e2d3d' : '#fff',
          border: `1px solid ${isDark ? 'rgba(255,255,255,.12)' : '#e2e8f0'}`,
          borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          padding: 20, minWidth: 360,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14,
            color: isDark ? '#e2e8f0' : '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
            📊 Prawn Market Rates
            <span style={{ fontSize: 11, fontWeight: 400, color: isDark ? '#64748b' : '#94a3b8', marginLeft: 'auto' }}>
              per kg (farm-gate)
            </span>
          </div>

          {/* Header row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 28px',
            gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 600,
            color: isDark ? '#64748b' : '#94a3b8', padding: '0 2px' }}>
            <span>Variety</span><span>Size/Grade</span><span>₹ / kg</span><span/>
          </div>

          {/* Editable rows */}
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 28px', gap: 6, marginBottom: 6 }}>
              <input value={r.variety} placeholder="e.g. Tiger"
                onChange={e => setRow(i, 'variety', e.target.value)}
                style={inputStyle(isDark)} />
              <input value={r.grade} placeholder="21/30"
                onChange={e => setRow(i, 'grade', e.target.value)}
                style={inputStyle(isDark)} />
              <input value={r.price} placeholder="₹ 480" type="number" min="0"
                onChange={e => setRow(i, 'price', e.target.value)}
                style={inputStyle(isDark)} />
              <button onClick={() => removeRow(i)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: '#ef4444', fontSize: 16, fontWeight: 900, lineHeight: 1,
                  padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
          ))}

          {/* Add row */}
          <button onClick={addRow}
            style={{ background: 'none', border: `1px dashed ${isDark ? '#334155' : '#cbd5e1'}`,
              borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
              color: isDark ? '#64748b' : '#94a3b8', width: '100%', marginTop: 4 }}>
            + Add variety
          </button>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, background: '#0ea5e9', color: '#fff', border: 'none',
                borderRadius: 20, padding: '7px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving…' : '💾 Save Rates'}
            </button>
            <button onClick={() => setEditing(false)}
              style={{ background: 'none', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                borderRadius: 20, padding: '7px 16px', fontSize: 13, cursor: 'pointer',
                color: isDark ? '#94a3b8' : '#64748b' }}>
              Cancel
            </button>
          </div>

          {updatedLabel && (
            <div style={{ fontSize: 10, color: isDark ? '#475569' : '#94a3b8', marginTop: 10, textAlign: 'right' }}>
              Last updated: {updatedLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function inputStyle(isDark) {
  return {
    padding: '5px 8px', borderRadius: 8, fontSize: 12,
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    background: isDark ? '#0f172a' : '#f8fafc',
    color: isDark ? '#e2e8f0' : '#1e293b',
    outline: 'none', width: '100%',
  };
}
